import type { MemberNotificationSettings } from '../../types/settings';

interface MemberNotificationControlsProps {
  memberId: string;
  memberName: string;
  value: MemberNotificationSettings;
  whatsappDeliverable: boolean;
  emailDeliverable: boolean;
  /**
   * STAFF callers see the settings but cannot change them (the server answers 403 anyway). The form
   * renders the visible explanation with id "member-notify-readonly-note".
   */
  readOnly: boolean;
  onChange: (next: MemberNotificationSettings) => void;
  t: (key: string) => string;
}

/**
 * Spec 076 — one member's notification settings in Edit members: a "Notifications" switch and the
 * "WhatsApp" / "Email" checkboxes, greyed while the switch is off (their values are kept). Every control
 * has a visible label wrapping it, so the whole label is the ≥44px hit area; flex + gap keeps the order
 * right in RTL. A muted hint explains a channel the member's contact cannot receive.
 */
export default function MemberNotificationControls({
  memberId, memberName, value, whatsappDeliverable, emailDeliverable, readOnly, onChange, t,
}: MemberNotificationControlsProps) {
  const id = (part: string) => `member-notify-${memberId}-${part}`;
  const channelsDisabled = readOnly || !value.enabled;

  return (
    <fieldset
      className="member-notify"
      data-testid={`member-notify-controls-${memberId}`}
      disabled={readOnly}
      aria-describedby={readOnly ? 'member-notify-readonly-note' : undefined}
    >
      <legend className="member-notify-legend">
        {t('settings.memberNotifications.title')} — {memberName}
      </legend>

      <label className="member-notify-option" htmlFor={id('enabled')}>
        <span className="toggle-switch toggle-switch-sm">
          <input
            id={id('enabled')}
            type="checkbox"
            role="switch"
            aria-checked={value.enabled}
            checked={value.enabled}
            disabled={readOnly}
            onChange={e => onChange({ ...value, enabled: e.target.checked })}
          />
          <span className="toggle-slider" aria-hidden="true" />
        </span>
        <span>{t('settings.memberNotifications.title')}</span>
      </label>

      <div className="member-notify-channels">
        <div className="member-notify-channel">
          <label className="member-notify-option" htmlFor={id('whatsapp')}>
            <input
              id={id('whatsapp')}
              type="checkbox"
              checked={value.whatsapp}
              disabled={channelsDisabled}
              aria-describedby={!whatsappDeliverable ? id('whatsapp-hint') : undefined}
              onChange={e => onChange({ ...value, whatsapp: e.target.checked })}
            />
            <span>{t('settings.memberNotifications.whatsapp')}</span>
          </label>
          {!whatsappDeliverable && (
            <span id={id('whatsapp-hint')} className="member-notify-hint">
              {t('settings.memberNotifications.noPhone')}
            </span>
          )}
        </div>

        <div className="member-notify-channel">
          <label className="member-notify-option" htmlFor={id('email')}>
            <input
              id={id('email')}
              type="checkbox"
              checked={value.email}
              disabled={channelsDisabled}
              aria-describedby={!emailDeliverable ? id('email-hint') : undefined}
              onChange={e => onChange({ ...value, email: e.target.checked })}
            />
            <span>{t('settings.memberNotifications.email')}</span>
          </label>
          {!emailDeliverable && (
            <span id={id('email-hint')} className="member-notify-hint">
              {t('settings.memberNotifications.noEmail')}
            </span>
          )}
        </div>
      </div>
    </fieldset>
  );
}
